import { z } from 'zod';
import { lenientString, lenientUuid, lenientEnum, lenientRequiredNode } from '../schema-extensions';
import { ExpenditureType } from '../enums/expenditure-type.enum';
import { Frequency } from '../enums/frequency.enum';
import { FrequencyValueNode } from './frequency-value-node-schema';

/**
 * Validation constants for ExpenditureNode
 * Defined before schemas to avoid forward reference issues
 */
const EXPENDITURE_CONSTANTS = {
  NAME_MAX_LENGTH: 100,
  REFERENCE_MAX_LENGTH: 100,
  DATE_FORMAT_LENGTH: 10,
  MIN_AMOUNT: 0,
} as const;

/**
 * Lenient schema for deserializing ExpenditureNode from storage
 * Handles missing fields and data migrations gracefully
 */
const ExpenditureNodeDeserializeSchema = z.object({
  id: lenientUuid(),
  type: lenientEnum(ExpenditureType, ExpenditureType.OTHER),
  name: lenientString(EXPENDITURE_CONSTANTS.NAME_MAX_LENGTH, ''),
  reference: lenientString(EXPENDITURE_CONSTANTS.REFERENCE_MAX_LENGTH, '').optional(),
  value: lenientRequiredNode(FrequencyValueNode)
}).transform(val => ({
  id: val.id,
  type: val.type,
  name: val.name,
  reference: val.reference,
  value: val.value
}));

/**
 * Strict schema for creating new ExpenditureNode
 * Enforces validation rules for new data
 */
const ExpenditureNodeCreateSchema = z.object({
  id: z.string().uuid(),
  type: z.nativeEnum(ExpenditureType),
  name: z.string().min(1, 'Name is required').max(EXPENDITURE_CONSTANTS.NAME_MAX_LENGTH),
  reference: z.string().max(EXPENDITURE_CONSTANTS.REFERENCE_MAX_LENGTH).optional(),
  value: z.unknown().transform(val => FrequencyValueNode.create(val as Partial<FrequencyValueNode>))
}).transform(val => ({
  id: val.id,
  type: val.type,
  name: val.name,
  reference: val.reference,
  value: val.value
}));

/**
 * ExpenditureNode interface
 * Represents a recurring or one-time expenditure
 */
export interface ExpenditureNode {
  id: string;
  type: ExpenditureType;
  name: string;
  reference?: string;
  value: FrequencyValueNode;
}

/**
 * ExpenditureNode namespace with factory and deserialization methods
 */
export namespace ExpenditureNode {
  /**
   * Validation constants for ExpenditureNode
   */
  export const Constants = EXPENDITURE_CONSTANTS;

  /**
   * Deserialize ExpenditureNode from unknown data (e.g., from storage)
   * Uses lenient validation with automatic defaults for missing fields
   * 
   * @param data Unknown data to deserialize
   * @returns Valid ExpenditureNode with defaults applied
   */
  export function deserialize(data: unknown): ExpenditureNode {
    return ExpenditureNodeDeserializeSchema.parse(data);
  }

  /**
   * Safely deserialize ExpenditureNode with error handling
   * 
   * @param data Unknown data to deserialize
   * @returns Success result with data or failure result with error
   */
  export function deserializeSafe(data: unknown): 
    | { success: true; data: ExpenditureNode }
    | { success: false; error: z.ZodError } 
  {
    const result = ExpenditureNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  /**
   * Create a new ExpenditureNode with strict validation
   * Use this for creating new expenditures from user input
   * 
   * @param data Partial expenditure data
   * @returns Valid ExpenditureNode
   * @throws ZodError if validation fails
   */
  export function create(data: Partial<ExpenditureNode>): ExpenditureNode {
    return ExpenditureNodeCreateSchema.parse({
      id: data.id ?? crypto.randomUUID(),
      ...data
    });
  }

  /**
   * Validate if data conforms to ExpenditureNode structure
   * 
   * @param data Data to validate
   * @returns True if data is a valid ExpenditureNode
   */
  export function validate(data: unknown): data is ExpenditureNode {
    return ExpenditureNodeDeserializeSchema.safeParse(data).success;
  }

  /**
   * Calculate annual expenditure amount
   * 
   * @param node ExpenditureNode to calculate for
   * @returns Annual expenditure amount
   */
  export function calculateAnnualAmount(node: ExpenditureNode): number {
    const { frequency, amount } = node.value;
    
    switch (frequency) {
      case Frequency.ANNUAL:
        return amount;
      case Frequency.MONTHLY:
        return amount * 12;
      case Frequency.WEEKLY:
        return amount * 52;
      case Frequency.DAILY:
        return amount * 365;
      case Frequency.ONE_TIME:
        return amount;
      default:
        return 0;
    }
  }

  /**
   * Check if expenditure is currently active based on date range
   * 
   * @param node ExpenditureNode to check
   * @param referenceDate Date to check against (defaults to today)
   * @returns True if expenditure is active on the reference date
   */
  export function isActive(node: ExpenditureNode, referenceDate: Date = new Date()): boolean {
    if (!node.value.from || !node.value.to) {
      return false;
    }

    const from = new Date(node.value.from);
    const to = new Date(node.value.to);
    
    return referenceDate >= from && referenceDate <= to;
  }
}
